'use client'

import { Link2, AtSign, Play, Mail, MapPin, Phone } from 'lucide-react'
import { useT } from '@/lib/useT'

const footerLinkKeys = {
  'footer.product': [
    { key: 'footer.features', href: '#features' },
    { key: 'footer.pricing', href: '#pricing' },
    { key: 'footer.integrations', href: '#' },
  ],
  'footer.company': [
    { key: 'footer.about', href: '#' },
    { key: 'footer.careers', href: '#' },
    { key: 'footer.blog', href: '#' },
  ],
  'footer.support': [
    { key: 'footer.docs', href: '#' },
    { key: 'footer.contact', href: '#' },
  ],
  'footer.legal': [
    { key: 'footer.privacy', href: '/privacy' },
    { key: 'footer.terms', href: '/terms' },
  ],
}

const social = [
  { icon: Link2, label: 'LinkedIn', href: '#' },
  { icon: AtSign, label: 'X (Twitter)', href: '#' },
  { icon: Play, label: 'YouTube', href: '#' },
]

export default function Footer() {
  const t = useT()

  return (
    <footer className="bg-[#0A171D]" role="contentinfo">
      {/* Waveform accent bar */}
      <div className="gradient-wave h-[3px] w-full opacity-70" aria-hidden="true" />

      <div className="mx-auto max-w-[1200px] px-6 pt-16 pb-10">
        {/* Top grid */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-10 mb-14">
          {/* Brand column — spans 2 cols */}
          <div className="col-span-2 flex flex-col gap-5">
            <a href="/" aria-label="Alnajah ERP — Home">
              <img
                src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/logo-symbol-VRM6CgqMUreEttB8e2HxyWH6JlfMgz.png"
                alt="Ajnihat Alnajah"
                className="h-9 w-auto brightness-0 invert opacity-90"
              />
            </a>
            <p className="text-sm text-[#FFF6E9]/55 leading-relaxed max-w-[220px]">
              Enterprise ERP built for the MENA region. Solutions Simplified.
            </p>

            {/* Contact mini-block */}
            <address className="not-italic flex flex-col gap-2.5 text-xs text-[#FFF6E9]/45">
              <span className="flex items-center gap-2">
                <MapPin size={12} aria-hidden="true" className="shrink-0 text-[#FFBD76]/60" />
                Olaya Business Center, Al Olaya, Al Olaya District, 12244 - Riyadh, KSA.
                Office No.26, 2nd Floor, Building 7505.
              </span>
              <a href="mailto:info@alnajahksa.com" className="flex items-center gap-2 hover:text-[#FFBD76] transition-colors duration-140">
                <Mail size={12} aria-hidden="true" className="shrink-0 text-[#FFBD76]/60" />
                info@alnajahksa.com
              </a>
              <a href="tel:+966535353311" className="flex items-center gap-2 hover:text-[#FFBD76] transition-colors duration-140">
                <Phone size={12} aria-hidden="true" className="shrink-0 text-[#FFBD76]/60" />
                +966 5353 53311
              </a>
            </address>

            {/* Social */}
            <div className="flex items-center gap-2">
              {social.map(({ icon: Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  className="flex h-8 w-8 items-center justify-center rounded-[8px] text-[#FFF6E9]/40 hover:text-[#FFBD76] hover:bg-[#FFF6E9]/6 transition-all duration-140 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC992]"
                >
                  <Icon size={16} aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(footerLinkKeys).map(([groupKey, links]) => (
            <div key={groupKey} className="flex flex-col gap-4">
              <h3 className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#FFF6E9]/35">
                {t(groupKey as any)}
              </h3>
              <ul className="flex flex-col gap-2" role="list">
                {links.map((link) => (
                  <li key={link.key}>
                    <a
                      href={link.href}
                      className="text-sm text-[#FFF6E9]/55 hover:text-[#FFBD76] transition-colors duration-[140ms] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#FFC992] rounded-sm"
                    >
                      {t(link.key as any)}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA band */}
        <div className="rounded-[18px] bg-[#003F47] border border-[#1A535A]/40 px-8 py-8 mb-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col gap-1 text-center md:text-left">
            <p className="text-base font-semibold text-[#FFFBF7]">{t('footer.ctaHeading')}</p>
            <p className="text-sm text-[#FFF6E9]/60">{t('footer.ctaDescription')}</p>
          </div>
          <a
            href="#signup"
            className="btn-press shrink-0 px-6 py-2.5 rounded-[999px] text-sm font-semibold text-[#0A171D] bg-[#FFBD76] hover:bg-[#F0A24E] transition-all duration-140 shadow-[0_8px_24px_rgba(255,189,118,0.35)] whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC992]"
          >
            {t('nav.getStarted')}
          </a>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-[#3B454A] pt-8">
          <p className="text-xs text-[#FFF6E9]/30">
            {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-4">
            <a href="/privacy" className="text-xs text-[#FFF6E9]/30 hover:text-[#FFF6E9]/60 transition-colors duration-140">{t('footer.privacy')}</a>
            <a href="/terms" className="text-xs text-[#FFF6E9]/30 hover:text-[#FFF6E9]/60 transition-colors duration-140">{t('footer.terms')}</a>
          </div>
        </div>
      </div>
    </footer>
  )
}
